<script setup>
import { ref, computed } from 'vue'
import { API, HOST } from '@/utils/http'
import { useStore } from 'vuex'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import LayerSelector from '@/components/chat/LayerSelector.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import { FEATURE_COUNT_CRITICAL_THRESHOLD } from '@/constants/features'

const props = defineProps({
  orgId: {
    type: [String, Number],
    required: true
  },
  open: {
    type: Boolean,
    default: false
  },
  connectedLayers: {
    type: Array,
    default: () => []
  },
  maxLayersReached: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:open', 'add'])

const store = useStore()
const selectedRange = computed(() => store.state.layers.selectedRange)
const selectedLayer = ref(null)
const selectedLayerFeatureCount = ref(0)
const isLoadingFeatures = ref(false)
const confirmDialog = ref(null)
const currentFetchRequest = ref(0) // Track request sequence to avoid race conditions

const isOpen = computed({
  get: () => props.open,
  set: (value) => emit('update:open', value)
})

const canAdd = computed(() => {
  return selectedLayer.value !== null && !props.maxLayersReached && !isLoadingFeatures.value
})

// Fetch feature count for selected layer
const fetchLayerFeatureCount = async (layer, requestId) => {
  if (!layer) return { count: 0, requestId }

  // Only fetch if date range is selected (match Layer Detail behavior)
  const [startDate, endDate] = selectedRange.value || []
  if (!startDate || !endDate) {
    // No date range selected - return 0 (same as Layer Detail)
    return { count: 0, requestId }
  }

  isLoadingFeatures.value = true
  try {
    // Build features URL with date filtering (same as ReportsList)
    const featuresUrl = `${HOST}/org/${props.orgId}/layers/${layer.code}/features?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`

    const response = await API({
      method: 'GET',
      url: featuresUrl
    })

    return { count: response.data.features?.length || 0, requestId }
  } catch (error) {
    console.error('Error fetching feature count:', error)
    return { count: 0, requestId }
  } finally {
    isLoadingFeatures.value = false
  }
}

// Handle layer selection
const handleLayerSelect = async (layer) => {
  selectedLayer.value = layer

  // Increment request counter to track this specific request
  const requestId = ++currentFetchRequest.value
  const result = await fetchLayerFeatureCount(layer, requestId)

  // Only update if this is still the most recent request
  if (result.requestId === currentFetchRequest.value) {
    selectedLayerFeatureCount.value = result.count
  }
}

// Calculate current total features from connected layers
const currentTotalFeatures = computed(() => {
  return props.connectedLayers.reduce((sum, layer) => sum + (layer.features_count || 0), 0)
})

// Calculate total features if this layer is added
const totalFeaturesIfAdded = computed(() => {
  return currentTotalFeatures.value + selectedLayerFeatureCount.value
})

// Safe dialog description with validated numeric values
const confirmDialogDescription = computed(() => {
  const total = Number(totalFeaturesIfAdded.value) || 0
  return `Adding this layer will bring your total to ${total} features. Queries with more than ${FEATURE_COUNT_CRITICAL_THRESHOLD} features may be expensive and slower to process. Do you want to continue?`
})

const handleAdd = () => {
  if (!canAdd.value) return

  // Check if adding this layer would exceed the limit
  const total = totalFeaturesIfAdded.value
  const wouldExceedLimit = total > FEATURE_COUNT_CRITICAL_THRESHOLD

  if (wouldExceedLimit && confirmDialog.value) {
    // Show confirmation dialog
    confirmDialog.value.open()
  } else {
    proceedWithAdd()
  }
}

const proceedWithAdd = () => {
  emit('add', selectedLayer.value)

  // Reset state
  selectedLayer.value = null
  selectedLayerFeatureCount.value = 0
}

const handleCancel = () => {
  selectedLayer.value = null
  selectedLayerFeatureCount.value = 0
  isOpen.value = false
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>Add Layer to Chat</DialogTitle>
        <DialogDescription>
          Select a layer to add to this chat session. The AI will analyze data from all connected layers.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4 py-4">
        <div v-if="maxLayersReached" class="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p class="text-sm text-yellow-800">
            Maximum of 10 layers reached. Please remove a layer before adding a new one.
          </p>
        </div>

        <div v-else class="space-y-2">
          <LayerSelector
            :org-id="orgId"
            :selected-layer="selectedLayer"
            :exclude-layers="connectedLayers.map(l => l.code)"
            @select="handleLayerSelect"
          />
          <div v-if="selectedLayerFeatureCount > 0" class="text-xs text-gray-500">
            This layer has {{ selectedLayerFeatureCount }} features
            <span v-if="currentTotalFeatures > 0">
              (total will be {{ totalFeaturesIfAdded }} features)
            </span>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button @click="handleCancel" variant="ghost">Cancel</Button>
        <Button @click="handleAdd" :disabled="!canAdd">
          {{ isLoadingFeatures ? 'Loading...' : 'Add Layer' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Confirmation dialog for high feature count -->
  <DialogConfirm
    ref="confirmDialog"
    title="High Feature Count Warning"
    :description="confirmDialogDescription"
    @confirm="proceedWithAdd"
  />
</template>
